/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
Container,
Typography,
Card,
CardContent,
Avatar,
Grid,
Chip,
IconButton,
useMediaQuery,
useTheme,
Slide,
Grow,
Zoom,
Alert,
CircularProgress,
Box,
Paper,
alpha,
CardActionArea,
Stack,
Skeleton,
Button,
InputAdornment,
TextField,
MenuItem,
Select,
InputLabel,
FormControl
} from '@mui/material';
import {
Twitter,
Facebook,
Instagram,
LinkedIn,
School,
Public,
Workspaces,
Search,
Science,
Refresh
} from '@mui/icons-material';
import { APIResearcherDetails, APIUniversitiesList } from '../../utilities/APIS';
import { getIdCategory } from '../../utilities/Functions';
import NavigationMenu from '../navigation/NavigationMenu';
import axios from 'axios';
import { BASE_URL, headers } from '../../utilities/Env';
import { TEXT_CONNECTION_ERROR, TEXT_DATA_LOADED } from '../../utilities/Text';
import useCustomToast from '../../hooks/useToast';
import { motion, AnimatePresence } from 'framer-motion';
import LoadingState from '../../components/LoadingState';

const Researchers = () => {
    const [researchersData, setResearchersData] = useState([]);
    const [universitiesData, setUniversitiesData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();

    const [searchTerm, setSearchTerm] = useState('');
    const [filteredData, setFilteredData] = useState([]);
    const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'


    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
            filterAndSortData();
        }, [researchersData, searchTerm,sortOrder]);


    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
        const [uniRes, resRes] = await Promise.all([
            axios.get(APIUniversitiesList, { headers }),
            axios.get(APIResearcherDetails, { headers }),
        ]);

        const res = Array.isArray(uniRes.data.data) ? uniRes.data.data.reverse() : [];
        setUniversitiesData(res);

        const data = Array.isArray(resRes.data.data) ? resRes.data.data.reverse() : [];
        setResearchersData(data);
            showToast("Success", TEXT_DATA_LOADED, "success");
        } catch (error) {
        let errorMessage = "Failed to load data";
        if (error.response) {
            errorMessage = error.response.data.message || error.message;
        } else if (error.code === "ERR_NETWORK") {
            errorMessage = TEXT_CONNECTION_ERROR;
        }
        setError(errorMessage);
        showToast("Error", errorMessage, "error");
        } finally {
        setLoading(false);
        }
    };

    const filterAndSortData = () => {
        let filtered = researchersData;

        // Filter by search term
        if (searchTerm) {
            filtered = filtered.filter(researcher =>
                researcher.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                researcher.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                getIdCategory(researcher.university, universitiesData)?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Sort data
        filtered = [...filtered].sort((a, b) => {
            let aValue = a.full_name?.toLowerCase() || ''; // Use an empty string if name is missing
            let bValue = b.full_name?.toLowerCase() || ''; // Use an empty string if name is missing

            // Standard comparison logic (assuming aValue and bValue are comparable, e.g., strings or numbers)
            if (sortOrder === 'asc') {
                // Ascending (a before b if a is 'less' than b)
                if (aValue < bValue) {
                    return -1;
                }
                if (aValue > bValue) {
                    return 1;
                }
                return 0; // values are equal
            } else {
                // Descending (a before b if a is 'greater' than b)
                if (aValue > bValue) {
                    return -1;
                }
                if (aValue < bValue) {
                    return 1;
                }
                return 0; // values are equal
            }
        });

        setFilteredData(filtered);
    };


    const handleResearcherClick = (researcherId) => {
        navigate(`/researchers/${researcherId}`);
    };

    const getLevelColor = (level) => {
        const colorMap = {
        'professor': 'error',
        'researcher': 'success',
        'student': 'info',
        'collaborator': 'warning',
        'phd': 'secondary'
        };
        return colorMap[level?.toLowerCase()] || 'primary';
    };

    const getSocialIconColor = (platform) => {
        const colorMap = {
        'twitter': '#1DA1F2',
        'facebook': '#1877F2',
        'instagram': '#E4405F',
        'linkedin': '#0A66C2'
        };
        return colorMap[platform] || theme.palette.primary.main;
    };

    const ResearcherCard = ({ researcher, index }) => (
        <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: index * 0.1 }}
        whileHover={{ y: -5 }}
        >
        <Card
            sx={{
            height: '100%',
            display: 'flex',
            width:350,
            flexDirection: 'column',
            borderRadius: 3,
            overflow: 'hidden',
            background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
            boxShadow: theme.shadows[2],
            transition: 'all 0.3s ease',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            '&:hover': {
                boxShadow: theme.shadows[8],
                transform: 'translateY(-8px)',
                borderColor: alpha(theme.palette.primary.main, 0.3),
            }
            }}
        >
            <CardActionArea 
            onClick={() => handleResearcherClick(researcher.id)}
            sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
            >
            {/* Profile Header */}
            <Box sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                py: 3,
                textAlign: 'center',
                position: 'relative'
            }}>
                <Avatar
                alt={researcher.full_name}
                src={researcher.profile_image ? `${BASE_URL}${researcher.profile_image}` : undefined}
                sx={{
                    width: 100,
                    height: 100,
                    border: `4px solid ${theme.palette.background.paper}`,
                    margin: '0 auto',
                    boxShadow: theme.shadows[4],
                }}
                >
                <Science sx={{ fontSize: 40 }} />
                </Avatar>
            </Box>

            <CardContent sx={{ flexGrow: 1, p: 3, textAlign: 'center' }}>
                {/* Name and Title */}
                <Typography variant="h6" component="h3" gutterBottom sx={{ 
                fontWeight: 600,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
                }}>
                {researcher.full_name}
                </Typography>
                
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {researcher.title}
                </Typography>

                {/* Level Chip */}
                <Chip
                icon={<Workspaces />}
                label={researcher.level}
                color={getLevelColor(researcher.level)}
                size="small"
                sx={{ 
                    mb: 2,
                    fontWeight: 600,
                    borderRadius: 2
                }}
                />

                {/* University */}
                <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mb: 2 }}>
                <School sx={{ fontSize: 18, color: 'primary.main' }} />
                <Typography variant="body2" color="text.secondary">
                    {getIdCategory(researcher.university, universitiesData)}
                </Typography>
                </Stack>

                {/* Nationality */}
                <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mb: 3 }}>
                <Public sx={{ fontSize: 16, color: 'primary.main' }} />
                <Typography variant="body2" color="text.secondary">
                    {researcher.nationality}
                </Typography>
                </Stack>

                {/* Social Links */}
                <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                gap: 1,
                flexWrap: 'wrap'
                }}>
                {researcher.linkedin && (
                    <IconButton 
                    component="a"
                    href={researcher.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    sx={{ 
                        backgroundColor: alpha(getSocialIconColor('linkedin'), 0.1),
                        '&:hover': { backgroundColor: alpha(getSocialIconColor('linkedin'), 0.2) }
                    }}
                    >
                    <LinkedIn sx={{ color: getSocialIconColor('linkedin'), fontSize: 20 }} />
                    </IconButton>
                )}
                {researcher.twitter && (
                    <IconButton 
                    component="a"
                    href={researcher.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    sx={{ 
                        backgroundColor: alpha(getSocialIconColor('twitter'), 0.1),
                        '&:hover': { backgroundColor: alpha(getSocialIconColor('twitter'), 0.2) }
                    }}
                    >
                    <Twitter sx={{ color: getSocialIconColor('twitter'), fontSize: 20 }} />
                    </IconButton>
                )}
                {researcher.facebook && (
                    <IconButton 
                    component="a"
                    href={researcher.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    sx={{ 
                        backgroundColor: alpha(getSocialIconColor('facebook'), 0.1),
                        '&:hover': { backgroundColor: alpha(getSocialIconColor('facebook'), 0.2) }
                    }}
                    >
                    <Facebook sx={{ color: getSocialIconColor('facebook'), fontSize: 20 }} />
                    </IconButton>
                )}
                {researcher.instagram && (
                    <IconButton 
                    component="a"
                    href={researcher.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    sx={{ 
                        backgroundColor: alpha(getSocialIconColor('instagram'), 0.1),
                        '&:hover': { backgroundColor: alpha(getSocialIconColor('instagram'), 0.2) }
                    }}
                    >
                    <Instagram sx={{ color: getSocialIconColor('instagram'), fontSize: 20 }} />
                    </IconButton>
                )}
                </Box>
            </CardContent>
            </CardActionArea>
        </Card>
        </motion.div>
    );

if (loading) {
    return (
        <>
            <NavigationMenu />
            <LoadingState/>
        </>
    );
}

if (error) {
    return (
    <Box sx={{ minHeight: '100vh', background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)` }}>
        <NavigationMenu />
        <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
            <Typography variant="h6">Error Loading Researchers</Typography>
            <Typography>{error}</Typography>
            <Button onClick={fetchData} variant="contained" sx={{ mt: 2 }}>
            Retry
            </Button>
        </Alert>
        </Container>
    </Box>
    );
}

return (
    <Box sx={{
    minHeight: '100vh',
    background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)` 
    }}>
    <NavigationMenu />

    <Container maxWidth="lg" sx={{ mt: 4, mb: 4,marginTop:10 }}>
        {/* Header Section */}
        <Slide direction="down" in={true} mountOnEnter unmountOnExit>
            <Box sx={{
            mb: 3,
            p: 2,
            borderRadius: 2,
            backgroundColor: theme.palette.success.main,
            color: theme.palette.primary.contrastText,
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2
            }}>
            <Box>
                <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold' }}>
                Author Professionals
                </Typography>
                <Typography variant="subtitle1">
                Meet our team of dedicated authors, professors, and academic professionals
                </Typography>
            </Box>
            </Box>
        </Slide>

        {/* Search and Filter Section */}
        <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
            <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
                <TextField
                fullWidth
                placeholder="By name, university, title..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                    startAdornment: (
                    <InputAdornment position="start">
                        <Search color="primary" />
                    </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                    <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchTerm('')}>
                        ✕
                        </IconButton>
                    </InputAdornment>
                    )
                }}
                />
            </Grid>
            <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                <InputLabel>Order</InputLabel>
                <Select
                    value={sortOrder}
                    label="Order"
                    onChange={(e) => setSortOrder(e.target.value)}
                >
                    <MenuItem value="asc">Ascending</MenuItem>
                    <MenuItem value="desc">Descending</MenuItem>
                </Select>
                </FormControl>
            </Grid>


            <Grid item xs={12} md={2}>
                <Button
                variant="outlined"
                onClick={fetchData}
                startIcon={<Refresh />}
                fullWidth
                >
                Refresh
                </Button>
            </Grid>
            </Grid>
            <br></br>
             {/* Results Count */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" color="text.secondary">
                Showing {filteredData.length} of {researchersData.length} Researchers
                </Typography>
                {searchTerm && (
                <Chip 
                    label={`Search: "${searchTerm}"`}
                    onDelete={() => setSearchTerm('')}
                    color="primary"
                    variant="outlined"
                />
                )}
            </Box>
        </Paper>
        {/* Researchers Grid */}
        <AnimatePresence>
        <Grid container spacing={3}>
            {filteredData.map((researcher, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={researcher.id}>
                <ResearcherCard researcher={researcher} index={index} />
            </Grid>
            ))}
        </Grid>
        </AnimatePresence>

        {/* Empty State */}
        {filteredData.length === 0 && (
        <Paper elevation={2} sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
            <Science sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
            No researchers found
            </Typography>
            <Typography variant="body1" color="text.secondary">
            Try adjusting your search criteria or filters
            </Typography>
        </Paper>
        )}
    </Container>
    </Box>
);
};

export default Researchers;